import React, { useState, useEffect } from 'react';
import {
  Settings,
  CheckCircle,
  AlertCircle,
  RefreshCw,
  Trash2,
  CloudCog,
  X,
  Plus,
  Phone,
  Building2,
  Clock,
  Shield,
  MessageSquare,
  ChevronDown,
  ChevronUp,
  Unplug,
  Power,
  Key,
} from 'lucide-react';
import { whatsappService } from 'api/services/whatsapp.service';
import {
  WhatsAppConfig,
  WhatsAppEmbeddedSignupConfig,
  WhatsAppResyncResponse,
  WhatsAppDisconnectResponse,
  RegisterPhoneNumberResponse,
} from 'types';
import EmbeddedSignup from 'components/WhatsApp/EmbeddedSignup';
import { useAuth } from 'hooks/useAuth';
import { LoadingSpinner } from 'components/Common/LoadingSpinner';

const WhatsAppSetupV2: React.FC = () => {
  const { currentOrganization } = useAuth();

  // Config state
  const [configs, setConfigs] = useState<WhatsAppConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Embedded signup state
  const [embeddedSignupConfig, setEmbeddedSignupConfig] = useState<WhatsAppEmbeddedSignupConfig | null>(null);
  const [loadingEmbeddedConfig, setLoadingEmbeddedConfig] = useState(false);
  const [embeddedConfigError, setEmbeddedConfigError] = useState<string | null>(null);

  // UI state
  const [showAddNew, setShowAddNew] = useState(false);
  const [expandedConfigId, setExpandedConfigId] = useState<string | null>(null);
  const [processingConfigId, setProcessingConfigId] = useState<string | null>(null);

  // Resync state
  const [resyncing, setResyncing] = useState(false);
  const [resyncResult, setResyncResult] = useState<WhatsAppResyncResponse | null>(null);
  const [showResyncModal, setShowResyncModal] = useState(false);

  // Delete state
  const [deletingConfigId, setDeletingConfigId] = useState<string | null>(null);

  // Disconnect state
  const [showDisconnectModal, setShowDisconnectModal] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [disconnectDeleteData, setDisconnectDeleteData] = useState(false);
  const [disconnectResult, setDisconnectResult] = useState<WhatsAppDisconnectResponse | null>(null);
  const [showDisconnectResultModal, setShowDisconnectResultModal] = useState(false);

  // Reactivate state
  const [showReactivateModal, setShowReactivateModal] = useState(false);
  const [reactivating, setReactivating] = useState(false);

  // Registration state
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [registeringConfigId, setRegisteringConfigId] = useState<string | null>(null);
  const [registerPin, setRegisterPin] = useState('');
  const [registerResult, setRegisterResult] = useState<RegisterPhoneNumberResponse | null>(null);
  const [showRegisterResultModal, setShowRegisterResultModal] = useState(false);
  const [registerError, setRegisterError] = useState<string | null>(null);

  useEffect(() => {
    fetchAllConfigs();
    if (currentOrganization?.id) {
      fetchEmbeddedSignupConfig(currentOrganization.id);
    }
  }, [currentOrganization]);

  const fetchAllConfigs = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = currentOrganization?.id
        ? await whatsappService.getAllConfigsForOrganization(currentOrganization.id)
        : await whatsappService.getAllConfigs();

      if (response.success && response.data) {
        setConfigs(response.data);
      } else {
        setConfigs([]);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load WhatsApp configurations');
    } finally {
      setLoading(false);
    }
  };

  const fetchEmbeddedSignupConfig = async (organizationId: string) => {
    setLoadingEmbeddedConfig(true);
    setEmbeddedConfigError(null);

    try {
      const response = await whatsappService.getEmbeddedSignupConfig(organizationId);
      if (response.success && response.data) {
        setEmbeddedSignupConfig(response.data);
      } else {
        setEmbeddedConfigError(response.error || 'Embedded signup not configured');
      }
    } catch (err: any) {
      setEmbeddedConfigError(err.message || 'Failed to fetch embedded signup configuration');
    } finally {
      setLoadingEmbeddedConfig(false);
    }
  };

  const handleResync = async () => {
    setResyncing(true);
    setResyncResult(null);

    const result = await whatsappService.resyncFromMeta(currentOrganization?.id);

    if (result.success && result.data) {
      setResyncResult(result.data);
      setShowResyncModal(true);
      await fetchAllConfigs();
    } else {
      alert(`Resync failed: ${result.error}`);
    }

    setResyncing(false);
  };

  const handleDeleteConfig = async (configId: string) => {
    if (!confirm('Are you sure you want to delete this WhatsApp number configuration? This action cannot be undone.')) {
      return;
    }

    setDeletingConfigId(configId);

    const result = await whatsappService.deleteConfigById(configId);

    if (result.success) {
      alert('WhatsApp configuration deleted successfully!');
      await fetchAllConfigs();
    } else {
      alert(`Failed to delete: ${result.error}`);
    }

    setDeletingConfigId(null);
  };

  const handleEmbeddedSignupSuccess = async (data: { code: string; wabaId: string; phoneNumberId?: string }) => {
    if (!currentOrganization?.id || !embeddedSignupConfig?.id) {
      alert('Missing organization or embedded signup configuration');
      return;
    }

    setProcessingConfigId('new');
    setError(null);

    try {
      const result = await whatsappService.processEmbeddedSignupCallback({
        businessOrganizationId: currentOrganization.id,
        embeddedSignupConfigId: embeddedSignupConfig.id,
        code: data.code,
        wabaId: data.wabaId,
        phoneNumberId: data.phoneNumberId,
      });

      if (result.success) {
        alert('WhatsApp Business Account Connected Successfully!');
        setShowAddNew(false);
        await fetchAllConfigs();
      } else {
        alert(`Setup failed: ${result.error}`);
      }
    } catch (err: any) {
      alert(`Error: ${err.message}`);
    } finally {
      setProcessingConfigId(null);
    }
  };

  const handleEmbeddedSignupError = (error: string) => {
    alert(`Embedded signup failed: ${error}`);
  };

  const handleDisconnect = async () => {
    setDisconnecting(true);
    setDisconnectResult(null);

    const result = await whatsappService.disconnectIntegration(currentOrganization?.id, disconnectDeleteData);

    if (result.success && result.data) {
      setDisconnectResult(result.data);
      setShowDisconnectModal(false);
      setShowDisconnectResultModal(true);
      setDisconnectDeleteData(false);
      await fetchAllConfigs();
    } else {
      alert(`Disconnect failed: ${result.error}`);
    }

    setDisconnecting(false);
  };

  const handleReactivateSuccess = async (data: { code: string; wabaId: string; phoneNumberId?: string }) => {
    if (!currentOrganization?.id || !embeddedSignupConfig?.id) {
      alert('Missing organization or embedded signup configuration');
      return;
    }

    setReactivating(true);

    try {
      // For reactivation, we use the callback which exchanges code for access token
      // The backend reactivate endpoint will be called internally with the new token
      const result = await whatsappService.processEmbeddedSignupCallback({
        businessOrganizationId: currentOrganization.id,
        embeddedSignupConfigId: embeddedSignupConfig.id,
        code: data.code,
        wabaId: data.wabaId,
        phoneNumberId: data.phoneNumberId,
      });

      if (result.success) {
        alert('WhatsApp integration reactivated successfully!');
        setShowReactivateModal(false);
        await fetchAllConfigs();
      } else {
        alert(`Reactivation failed: ${result.error}`);
      }
    } catch (err: any) {
      alert(`Error: ${err.message}`);
    } finally {
      setReactivating(false);
    }
  };

  const openRegisterModal = (configId: string) => {
    setRegisteringConfigId(configId);
    setRegisterPin('');
    setRegisterError(null);
    setShowRegisterModal(true);
  };

  const handleRegisterPhone = async () => {
    if (!registeringConfigId) return;

    // Validate PIN
    if (!registerPin || registerPin.length !== 6 || !/^\d{6}$/.test(registerPin)) {
      setRegisterError('Please enter a valid 6-digit PIN');
      return;
    }

    setRegisterError(null);
    const result = await whatsappService.registerPhoneNumberByConfigId(registeringConfigId, registerPin);

    if (result.success && result.data) {
      setRegisterResult(result.data);
      setShowRegisterModal(false);
      setShowRegisterResultModal(true);
      await fetchAllConfigs();
    } else {
      setRegisterError(result.error || 'Registration failed');
    }

    setRegisteringConfigId(null);
    setRegisterPin('');
  };

  const getRegistrationBadge = (status?: string) => {
    if (!status) return null;
    const styles: Record<string, { bg: string; text: string }> = {
      REGISTERED: {
        bg: 'bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-400',
        text: 'Registered',
      },
      NOT_REGISTERED: {
        bg: 'bg-yellow-100 dark:bg-yellow-900/40 text-yellow-800 dark:text-yellow-400',
        text: 'Not Registered',
      },
      REGISTRATION_FAILED: {
        bg: 'bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-400',
        text: 'Registration Failed',
      },
    };
    const style = styles[status] || styles.NOT_REGISTERED;
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${style.bg}`}>
        {style.text}
      </span>
    );
  };

  const hasInactiveConfigs = configs.some((c) => c.active === false);
  const hasActiveConfigs = configs.some((c) => c.active !== false);

  const getQualityBadge = (rating?: string) => {
    if (!rating) return null;
    const colors: Record<string, string> = {
      GREEN: 'bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-400',
      YELLOW: 'bg-yellow-100 dark:bg-yellow-900/40 text-yellow-800 dark:text-yellow-400',
      RED: 'bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-400',
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${colors[rating] || colors.GREEN}`}>
        {rating}
      </span>
    );
  };

  const getStatusBadge = (config: WhatsAppConfig) => {
    const isActive = config.active !== false;
    return (
      <span
        className={`px-2 py-1 rounded-full text-xs font-medium ${
          isActive
            ? 'bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-400'
            : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
        }`}
      >
        {isActive ? 'Active' : 'Inactive'}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 dark:text-white flex items-center gap-2">
            <Settings className="w-7 h-7" />
            WhatsApp Configuration
          </h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Manage your WhatsApp Business numbers and configurations
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {/* Reactivate button - show when there are inactive configs */}
          {hasInactiveConfigs && (
            <button
              onClick={() => setShowReactivateModal(true)}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2 transition"
              title="Reactivate deactivated WhatsApp integration"
            >
              <Power className="w-4 h-4" />
              Reactivate
            </button>
          )}
          {/* Disconnect button - show when there are active configs */}
          {hasActiveConfigs && (
            <button
              onClick={() => setShowDisconnectModal(true)}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center gap-2 transition"
              title="Disconnect WhatsApp integration"
            >
              <Unplug className="w-4 h-4" />
              Disconnect
            </button>
          )}
          {configs.length > 0 && (
            <button
              onClick={handleResync}
              disabled={resyncing}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition"
              title="Resync all phone numbers and templates from Meta"
            >
              {resyncing ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  Resyncing...
                </>
              ) : (
                <>
                  <CloudCog className="w-4 h-4" />
                  Resync All
                </>
              )}
            </button>
          )}
          <button
            onClick={() => setShowAddNew(!showAddNew)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2 transition"
          >
            <Plus className="w-4 h-4" />
            Add Number
          </button>
        </div>
      </div>

      {/* Summary Stats */}
      {configs.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 flex items-center gap-4">
            <div className="p-3 bg-blue-100 dark:bg-blue-900/40 rounded-lg">
              <Phone className="w-6 h-6 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{configs.length}</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">Phone Numbers</p>
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 flex items-center gap-4">
            <div className="p-3 bg-green-100 dark:bg-green-900/40 rounded-lg">
              <CheckCircle className="w-6 h-6 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {configs.filter((c) => c.active !== false).length}
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400">Active</p>
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 flex items-center gap-4">
            <div className="p-3 bg-purple-100 dark:bg-purple-900/40 rounded-lg">
              <Building2 className="w-6 h-6 text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {new Set(configs.map((c) => c.businessAccountId)).size}
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400">Business Accounts</p>
            </div>
          </div>
        </div>
      )}

      {/* Add New Number Section */}
      {showAddNew && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold text-gray-800 dark:text-white">
              Add New WhatsApp Number
            </h2>
            <button
              onClick={() => setShowAddNew(false)}
              className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4 mb-6">
            <h3 className="font-semibold text-green-900 dark:text-green-100 mb-2">
              Connect via Facebook Login
            </h3>
            <p className="text-sm text-green-800 dark:text-green-300">
              Connect your WhatsApp Business Account in just a few clicks. You can add multiple
              phone numbers from the same or different business accounts.
            </p>
          </div>

          {loadingEmbeddedConfig && (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="w-6 h-6 animate-spin text-blue-600" />
              <span className="ml-3 text-gray-600 dark:text-gray-400">Loading...</span>
            </div>
          )}

          {embeddedConfigError && !loadingEmbeddedConfig && (
            <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-medium text-yellow-900 dark:text-yellow-100">
                    Embedded Signup Not Configured
                  </h4>
                  <p className="text-sm text-yellow-800 dark:text-yellow-300 mt-1">
                    {embeddedConfigError}
                  </p>
                </div>
              </div>
            </div>
          )}

          {embeddedSignupConfig && !loadingEmbeddedConfig && (
            <EmbeddedSignup
              appId={embeddedSignupConfig.facebookAppId}
              configId={embeddedSignupConfig.configurationId}
              solutionId={embeddedSignupConfig.solutionId}
              onSuccess={handleEmbeddedSignupSuccess}
              onError={handleEmbeddedSignupError}
            />
          )}

          {processingConfigId === 'new' && (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="w-6 h-6 animate-spin text-blue-600" />
              <span className="ml-3 text-gray-600 dark:text-gray-400">
                Processing signup...
              </span>
            </div>
          )}
        </div>
      )}

      {/* No Configurations */}
      {configs.length === 0 && !showAddNew && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-12 text-center">
          <Phone className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-800 dark:text-white mb-2">
            No WhatsApp Numbers Configured
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            Get started by adding your first WhatsApp Business number.
          </p>
          <button
            onClick={() => setShowAddNew(true)}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2 mx-auto transition"
          >
            <Plus className="w-5 h-5" />
            Add Your First Number
          </button>
        </div>
      )}

      {/* Configurations List */}
      {configs.length > 0 && (
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-gray-800 dark:text-white">
            Connected Numbers ({configs.length})
          </h2>

          {configs.map((config) => (
            <div
              key={config.id}
              className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden"
            >
              {/* Card Header */}
              <div
                className="p-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 transition"
                onClick={() =>
                  setExpandedConfigId(expandedConfigId === config.id ? null : config.id || null)
                }
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="p-2 bg-green-100 dark:bg-green-900/40 rounded-lg">
                      <Phone className="w-5 h-5 text-green-600 dark:text-green-400" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-semibold text-gray-900 dark:text-white">
                          {config.phoneNumber || 'Unknown Number'}
                        </h3>
                        {getStatusBadge(config)}
                        {getQualityBadge(config.qualityRating)}
                        {getRegistrationBadge(config.registrationStatus)}
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {config.displayName || 'No display name'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {config.messagingLimitTier && (
                      <span className="hidden sm:flex items-center gap-1 text-sm text-gray-600 dark:text-gray-400">
                        <MessageSquare className="w-4 h-4" />
                        {config.messagingLimitTier}
                      </span>
                    )}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (config.id) handleDeleteConfig(config.id);
                      }}
                      disabled={deletingConfigId === config.id}
                      className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition disabled:opacity-50"
                      title="Delete this number"
                    >
                      {deletingConfigId === config.id ? (
                        <RefreshCw className="w-4 h-4 animate-spin" />
                      ) : (
                        <Trash2 className="w-4 h-4" />
                      )}
                    </button>
                    {expandedConfigId === config.id ? (
                      <ChevronUp className="w-5 h-5 text-gray-400" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-400" />
                    )}
                  </div>
                </div>
              </div>

              {/* Expanded Details */}
              {expandedConfigId === config.id && (
                <div className="border-t border-gray-200 dark:border-gray-700 p-4 bg-gray-50 dark:bg-gray-700/30">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                        Phone Number ID
                      </p>
                      <p className="text-sm font-medium text-gray-900 dark:text-white mt-1">
                        {config.phoneNumberId}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                        Business Account ID
                      </p>
                      <p className="text-sm font-medium text-gray-900 dark:text-white mt-1">
                        {config.businessAccountId}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                        Organization ID
                      </p>
                      <p className="text-sm font-medium text-gray-900 dark:text-white mt-1">
                        {config.organizationId}
                      </p>
                    </div>
                    {config.qualityRating && (
                      <div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                          Quality Rating
                        </p>
                        <div className="mt-1">{getQualityBadge(config.qualityRating)}</div>
                      </div>
                    )}
                    {config.messagingLimitTier && (
                      <div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                          Messaging Limit
                        </p>
                        <p className="text-sm font-medium text-gray-900 dark:text-white mt-1">
                          {config.messagingLimitTier}
                        </p>
                      </div>
                    )}
                    {config.createdAt && (
                      <div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                          Created At
                        </p>
                        <p className="text-sm font-medium text-gray-900 dark:text-white mt-1 flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {new Date(config.createdAt).toLocaleDateString()}
                        </p>
                      </div>
                    )}
                    {config.lastSyncedAt && (
                      <div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                          Last Synced
                        </p>
                        <p className="text-sm font-medium text-gray-900 dark:text-white mt-1 flex items-center gap-1">
                          <RefreshCw className="w-3 h-3" />
                          {new Date(config.lastSyncedAt).toLocaleString()}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Webhook Info */}
                  <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                    <div className="flex items-center gap-2 text-blue-800 dark:text-blue-300">
                      <Shield className="w-4 h-4" />
                      <span className="text-sm font-medium">Webhook Configuration</span>
                    </div>
                    <p className="text-xs text-blue-700 dark:text-blue-400 mt-1">
                      Verify Token: ••••••••{' '}
                      <span className="text-blue-600 dark:text-blue-300">(configured)</span>
                    </p>
                  </div>

                  {/* Registration Info */}
                  <div className="mt-4 p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-purple-800 dark:text-purple-300">
                        <Key className="w-4 h-4" />
                        <span className="text-sm font-medium">Meta Registration</span>
                      </div>
                      {getRegistrationBadge(config.registrationStatus)}
                    </div>
                    <p className="text-xs text-purple-700 dark:text-purple-400 mt-1">
                      {config.registrationStatus === 'REGISTERED'
                        ? `Registered at: ${config.registeredAt ? new Date(config.registeredAt).toLocaleString() : 'N/A'}`
                        : 'Phone number needs to be registered with Meta to send messages'}
                    </p>
                    {config.registrationStatus !== 'REGISTERED' && config.id && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          openRegisterModal(config.id!);
                        }}
                        className="mt-2 px-3 py-1.5 bg-purple-600 text-white text-sm rounded-lg hover:bg-purple-700 flex items-center gap-1 transition"
                      >
                        <Key className="w-4 h-4" />
                        Register with Meta
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Resync Results Modal */}
      {showResyncModal && resyncResult && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => setShowResyncModal(false)}
        >
          <div
            className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-start mb-4">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-6 h-6 text-green-500" />
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                  Resync Completed
                </h2>
              </div>
              <button
                onClick={() => setShowResyncModal(false)}
                className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3">
                <p className="text-sm text-gray-600 dark:text-gray-400">WABA ID</p>
                <p className="font-medium text-gray-900 dark:text-white">{resyncResult.wabaId}</p>
              </div>

              <div className="bg-blue-50 dark:bg-blue-900/30 rounded-lg p-4">
                <h3 className="font-semibold text-blue-900 dark:text-blue-100 mb-2">
                  Phone Numbers
                </h3>
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                      {resyncResult.phoneNumbersFromMeta}
                    </p>
                    <p className="text-xs text-blue-700 dark:text-blue-300">From Meta</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                      {resyncResult.phoneNumbersAdded}
                    </p>
                    <p className="text-xs text-green-700 dark:text-green-300">Added</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">
                      {resyncResult.phoneNumbersUpdated}
                    </p>
                    <p className="text-xs text-yellow-700 dark:text-yellow-300">Updated</p>
                  </div>
                </div>
              </div>

              <div className="bg-purple-50 dark:bg-purple-900/30 rounded-lg p-4">
                <h3 className="font-semibold text-purple-900 dark:text-purple-100 mb-2">
                  Templates
                </h3>
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                      {resyncResult.templatesFromMeta}
                    </p>
                    <p className="text-xs text-purple-700 dark:text-purple-300">From Meta</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                      {resyncResult.templatesImported}
                    </p>
                    <p className="text-xs text-green-700 dark:text-green-300">Imported</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">
                      {resyncResult.templatesUpdated}
                    </p>
                    <p className="text-xs text-yellow-700 dark:text-yellow-300">Updated</p>
                  </div>
                </div>
              </div>

              <div className="text-center text-sm text-gray-500 dark:text-gray-400">
                Synced at: {new Date(resyncResult.syncedAt).toLocaleString()}
              </div>

              <button
                onClick={() => setShowResyncModal(false)}
                className="w-full px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Disconnect Confirmation Modal */}
      {showDisconnectModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => !disconnecting && setShowDisconnectModal(false)}
        >
          <div
            className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-start mb-4">
              <div className="flex items-center gap-2">
                <Unplug className="w-6 h-6 text-red-500" />
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                  Disconnect WhatsApp
                </h2>
              </div>
              <button
                onClick={() => !disconnecting && setShowDisconnectModal(false)}
                className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
                disabled={disconnecting}
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                <p className="text-sm text-red-800 dark:text-red-300">
                  This will disconnect your WhatsApp Business integration. All phone numbers will be deactivated and webhooks will be unsubscribed.
                </p>
              </div>

              <div className="space-y-3">
                <label className="flex items-start gap-3 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg cursor-pointer">
                  <input
                    type="radio"
                    name="deleteData"
                    checked={!disconnectDeleteData}
                    onChange={() => setDisconnectDeleteData(false)}
                    className="mt-1"
                  />
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">
                      Deactivate Only (Recommended)
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Preserves your configurations, templates, and registrations. You can reactivate later.
                    </p>
                  </div>
                </label>

                <label className="flex items-start gap-3 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg cursor-pointer">
                  <input
                    type="radio"
                    name="deleteData"
                    checked={disconnectDeleteData}
                    onChange={() => setDisconnectDeleteData(true)}
                    className="mt-1"
                  />
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white text-red-600 dark:text-red-400">
                      Delete Everything
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Permanently deletes all configurations, templates, and registrations. This cannot be undone.
                    </p>
                  </div>
                </label>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setShowDisconnectModal(false)}
                  disabled={disconnecting}
                  className="flex-1 px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDisconnect}
                  disabled={disconnecting}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {disconnecting ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      Disconnecting...
                    </>
                  ) : (
                    <>
                      <Unplug className="w-4 h-4" />
                      Disconnect
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Disconnect Result Modal */}
      {showDisconnectResultModal && disconnectResult && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => setShowDisconnectResultModal(false)}
        >
          <div
            className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-start mb-4">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-6 h-6 text-green-500" />
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                  Disconnected Successfully
                </h2>
              </div>
              <button
                onClick={() => setShowDisconnectResultModal(false)}
                className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3">
                <p className="text-sm text-gray-600 dark:text-gray-400">WABA ID</p>
                <p className="font-medium text-gray-900 dark:text-white">{disconnectResult.wabaId}</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="bg-blue-50 dark:bg-blue-900/30 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                    {disconnectResult.configsProcessed}
                  </p>
                  <p className="text-xs text-blue-700 dark:text-blue-300">Configs Processed</p>
                </div>
                <div className="bg-purple-50 dark:bg-purple-900/30 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                    {disconnectResult.registrationsProcessed}
                  </p>
                  <p className="text-xs text-purple-700 dark:text-purple-300">Registrations</p>
                </div>
              </div>

              <div className="flex items-center gap-4 text-sm">
                <div className="flex items-center gap-2">
                  {disconnectResult.webhooksUnsubscribed ? (
                    <CheckCircle className="w-4 h-4 text-green-500" />
                  ) : (
                    <AlertCircle className="w-4 h-4 text-yellow-500" />
                  )}
                  <span className="text-gray-600 dark:text-gray-400">Webhooks Unsubscribed</span>
                </div>
                <div className="flex items-center gap-2">
                  {disconnectResult.dataDeleted ? (
                    <span className="text-red-600 dark:text-red-400 flex items-center gap-1">
                      <Trash2 className="w-4 h-4" />
                      Data Deleted
                    </span>
                  ) : (
                    <span className="text-green-600 dark:text-green-400 flex items-center gap-1">
                      <Shield className="w-4 h-4" />
                      Data Preserved
                    </span>
                  )}
                </div>
              </div>

              {disconnectResult.dataDeleted && disconnectResult.templatesDeleted > 0 && (
                <div className="bg-red-50 dark:bg-red-900/30 rounded-lg p-3">
                  <p className="text-sm text-red-800 dark:text-red-300">
                    {disconnectResult.templatesDeleted} templates were deleted
                  </p>
                </div>
              )}

              <div className="text-center text-sm text-gray-500 dark:text-gray-400">
                Disconnected at: {new Date(disconnectResult.disconnectedAt).toLocaleString()}
              </div>

              <button
                onClick={() => setShowDisconnectResultModal(false)}
                className="w-full px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reactivate Modal */}
      {showReactivateModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => !reactivating && setShowReactivateModal(false)}
        >
          <div
            className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-lg w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-start mb-4">
              <div className="flex items-center gap-2">
                <Power className="w-6 h-6 text-green-500" />
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                  Reactivate WhatsApp Integration
                </h2>
              </div>
              <button
                onClick={() => !reactivating && setShowReactivateModal(false)}
                className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
                disabled={reactivating}
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
                <h3 className="font-semibold text-green-900 dark:text-green-100 mb-2">
                  Re-authenticate with Facebook
                </h3>
                <p className="text-sm text-green-800 dark:text-green-300">
                  To reactivate your WhatsApp integration, you need to re-authenticate with Facebook to get a new access token. Your existing configurations will be restored.
                </p>
              </div>

              {loadingEmbeddedConfig && (
                <div className="flex items-center justify-center py-8">
                  <RefreshCw className="w-6 h-6 animate-spin text-blue-600" />
                  <span className="ml-3 text-gray-600 dark:text-gray-400">Loading...</span>
                </div>
              )}

              {embeddedConfigError && !loadingEmbeddedConfig && (
                <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <h4 className="font-medium text-yellow-900 dark:text-yellow-100">
                        Embedded Signup Not Configured
                      </h4>
                      <p className="text-sm text-yellow-800 dark:text-yellow-300 mt-1">
                        {embeddedConfigError}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {embeddedSignupConfig && !loadingEmbeddedConfig && (
                <EmbeddedSignup
                  appId={embeddedSignupConfig.facebookAppId}
                  configId={embeddedSignupConfig.configurationId}
                  solutionId={embeddedSignupConfig.solutionId}
                  onSuccess={handleReactivateSuccess}
                  onError={handleEmbeddedSignupError}
                />
              )}

              {reactivating && (
                <div className="flex items-center justify-center py-8">
                  <RefreshCw className="w-6 h-6 animate-spin text-green-600" />
                  <span className="ml-3 text-gray-600 dark:text-gray-400">
                    Reactivating integration...
                  </span>
                </div>
              )}

              <button
                onClick={() => setShowReactivateModal(false)}
                disabled={reactivating}
                className="w-full px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Registration PIN Modal */}
      {showRegisterModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => {
            if (!registeringConfigId) {
              setShowRegisterModal(false);
              setRegisterPin('');
              setRegisterError(null);
            }
          }}
        >
          <div
            className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-start mb-4">
              <div className="flex items-center gap-2">
                <Key className="w-6 h-6 text-purple-500" />
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                  Register Phone Number
                </h2>
              </div>
              <button
                onClick={() => {
                  setShowRegisterModal(false);
                  setRegisterPin('');
                  setRegisterError(null);
                }}
                disabled={!!registeringConfigId}
                className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-4">
                <p className="text-sm text-purple-800 dark:text-purple-300">
                  Enter a 6-digit PIN to register this phone number with Meta. This PIN is used for
                  two-factor authentication and will be stored for future reference.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  6-Digit PIN
                </label>
                <input
                  type="text"
                  value={registerPin}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                    setRegisterPin(val);
                    setRegisterError(null);
                  }}
                  placeholder="Enter 6-digit PIN"
                  maxLength={6}
                  className="w-full px-4 py-3 text-2xl tracking-widest text-center border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-mono"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 text-center">
                  {registerPin.length}/6 digits
                </p>
              </div>

              {registerError && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
                  <div className="flex items-center gap-2 text-red-800 dark:text-red-300">
                    <AlertCircle className="w-4 h-4" />
                    <span className="text-sm">{registerError}</span>
                  </div>
                </div>
              )}

              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => {
                    setShowRegisterModal(false);
                    setRegisterPin('');
                    setRegisterError(null);
                  }}
                  disabled={!!registeringConfigId}
                  className="flex-1 px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleRegisterPhone}
                  disabled={!!registeringConfigId || registerPin.length !== 6}
                  className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {registeringConfigId ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      Registering...
                    </>
                  ) : (
                    <>
                      <Key className="w-4 h-4" />
                      Register
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Registration Result Modal */}
      {showRegisterResultModal && registerResult && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => setShowRegisterResultModal(false)}
        >
          <div
            className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-start mb-4">
              <div className="flex items-center gap-2">
                {registerResult.success ? (
                  <CheckCircle className="w-6 h-6 text-green-500" />
                ) : (
                  <AlertCircle className="w-6 h-6 text-red-500" />
                )}
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                  {registerResult.success ? 'Registration Successful' : 'Registration Failed'}
                </h2>
              </div>
              <button
                onClick={() => setShowRegisterResultModal(false)}
                className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3">
                <p className="text-sm text-gray-600 dark:text-gray-400">Phone Number ID</p>
                <p className="font-medium text-gray-900 dark:text-white font-mono">
                  {registerResult.phoneNumberId}
                </p>
              </div>

              <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3">
                <p className="text-sm text-gray-600 dark:text-gray-400">Registration Status</p>
                <div className="mt-1">{getRegistrationBadge(registerResult.registrationStatus)}</div>
              </div>

              {registerResult.response && (
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3">
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Response Details</p>
                  <pre className="text-xs text-gray-700 dark:text-gray-300 overflow-x-auto">
                    {JSON.stringify(registerResult.response, null, 2)}
                  </pre>
                </div>
              )}

              <button
                onClick={() => setShowRegisterResultModal(false)}
                className="w-full px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WhatsAppSetupV2;
